import React, { memo, useCallback, useMemo, useState } from 'react'
import { IWorkorderContextExpose, WorkorderContext } from 'contexts/WorkorderContext/WorkorderContext'
import { useDetermineWorkorderUUIDForContext } from 'contexts/WorkorderContext/use-determine-workorder-uuid-for-context'
import { useGetCurrentOpenOrder } from 'contexts/WorkorderContext/use-get-current-open-order'
import { WorkorderSelectionDialog } from 'contexts/WorkorderContext/WorkorderSelectionDialog'

type WoDialogResolverState = { resolve: (selectedWoUUID: string) => void; reject: (reason?: string) => void } | null

const MemoWorkorderContextProvider: React.FC<{ value: IWorkorderContextExpose }> = memo(props => (
  <WorkorderContext.Provider value={props.value}>{props.children}</WorkorderContext.Provider>
))

const _WorkorderContextProvider: React.FC = ({ children }) => {
  const currentOpenOrder = useGetCurrentOpenOrder()
  const [dialogResolver, setDialogResolver] = useState<WoDialogResolverState>(null)
  const [dialogVehicleUUID, setDialogVehicleUUID] = useState('')
  const [dialogAccountUUID, setDialogAccountUUID] = useState('')
  const openDialog = useCallback((vehicleUUID?: string, accountUUID?: string) => {
    setDialogAccountUUID(accountUUID || '')
    setDialogVehicleUUID(vehicleUUID || '')
    return new Promise<string>((resolve, reject) => setDialogResolver({ resolve, reject }))
  }, [])
  const onCloseDialog = useCallback(() => {
    dialogResolver?.reject?.('closed dialog')
    setDialogResolver(null)
  }, [dialogResolver, setDialogResolver, setDialogAccountUUID, setDialogVehicleUUID])
  const onSelectInDialog = useCallback(
    (uuid: string) => {
      dialogResolver?.resolve?.(uuid)
      setDialogResolver(null)
    },
    [setDialogResolver, dialogResolver]
  )

  const determineWorkorderUUID = useDetermineWorkorderUUIDForContext({ onOpenDialog: openDialog })
  // make sure context does not rerender because 'children' changed, do not compose inside render of provider!
  const memoChildren = useMemo(
    () => (
      <>
        {children}
        <WorkorderSelectionDialog
          open={!!dialogResolver}
          vehicleUUID={dialogVehicleUUID}
          accountUUID={dialogAccountUUID}
          onClose={onCloseDialog}
          onSelect={onSelectInDialog}
        />
      </>
    ),
    [children, dialogResolver, dialogVehicleUUID, dialogAccountUUID, onCloseDialog, onSelectInDialog]
  )

  const value = useMemo<IWorkorderContextExpose>(() => {
    return {
      hasBeenProvided: true,
      isInContext: !!currentOpenOrder,
      getWorkorderUUID: determineWorkorderUUID
    }
  }, [currentOpenOrder, determineWorkorderUUID])

  return <MemoWorkorderContextProvider value={value}>{memoChildren}</MemoWorkorderContextProvider>
}

const WorkorderContextProvider: React.FC = memo(_WorkorderContextProvider)
export default WorkorderContextProvider
